import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { whatsappColors } from '@/components/conversations/types';
import { ConversationList } from '@/components/conversations/ConversationList';
import { ChatArea } from '@/components/conversations/ChatArea';
import { ChatComposer } from '@/components/conversations/ChatComposer';
import { ResizablePanel } from '@/components/conversations/ResizablePanel';
import { useConversations } from '@/hooks';
import { useConversationMessages } from '@/hooks/useConversationMessages';
import { sendTextMessage } from '@/services/messagesService';
import type { Conversation } from '@/services/conversationsService';
import type { ConversationItemData, MessageGroup, MessageData } from '@/components/conversations/types';

export function ConversationsV2() {
  const params = useParams<{ id?: string }>();
  const selectedConversationId = params.id;
  const navigate = useNavigate();
  
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'open' | 'closed'>('all');
  const [sending, setSending] = useState(false);

  // Fetch conversations
  const conversationsQuery = useConversations({ limit: 100 });
  const conversations = (conversationsQuery.data?.data ?? []) as Conversation[];

  // Find selected conversation
  const selectedConversation = conversations.find((c) => c.id === selectedConversationId);

  // Fetch messages for selected conversation only
  const messagesQuery = useConversationMessages({
    conversationId: selectedConversationId || '',
    limit: 200,
    enabled: !!selectedConversationId,
    refetchIntervalMs: 3000,
  });

  // Check if window is closed
  const isWindowClosed = useMemo(() => {
    if (!selectedConversation?.window_expires_at) return false;
    return new Date(selectedConversation.window_expires_at) <= new Date();
  }, [selectedConversation]);

  // Transform conversations to UI format
  const conversationItems = useMemo((): ConversationItemData[] => {
    const term = search.trim().toLowerCase();
    
    // Sort by last message date
    const sorted = [...conversations].sort((a, b) => {
      const at = a.last_message?.created_at || a.last_message_at || a.opened_at;
      const bt = b.last_message?.created_at || b.last_message_at || b.opened_at;
      return new Date(bt).getTime() - new Date(at).getTime();
    });

    // Filter by search
    let filtered = term
      ? sorted.filter((c) => {
          const contact = c.contacts?.name || c.contacts?.phone || c.contact_id;
          const lastText = c.last_message?.type === 'text' ? c.last_message?.text : '';
          return (
            String(contact ?? '').toLowerCase().includes(term) ||
            String(lastText ?? '').toLowerCase().includes(term)
          );
        })
      : sorted;

    // Filter by window status
    if (filterStatus !== 'all') {
      filtered = filtered.filter((c) => {
        const isClosed = c.window_expires_at 
          ? new Date(c.window_expires_at) <= new Date()
          : false;
        return filterStatus === 'closed' ? isClosed : !isClosed;
      });
    }

    // Transform to UI format
    return filtered.map((c): ConversationItemData => {
      const contactName = c.contacts?.name || c.contacts?.phone || c.contact_id || 'Contato';
      const lastMsg = c.last_message;
      const lastMsgText = lastMsg?.type === 'text' 
        ? (lastMsg.direction === 'outbound' ? 'Você: ' : '') + (lastMsg.text || '')
        : lastMsg ? 'Template' : 'Sem mensagens';
      
      const lastTime = lastMsg?.created_at || c.last_message_at || c.opened_at;
      const lastTimeStr = formatTime(lastTime);
      
      const windowClosed = c.window_expires_at 
        ? new Date(c.window_expires_at) <= new Date()
        : false;

      return {
        id: c.id,
        contactName,
        contactPhone: c.contacts?.phone || c.contact_id || '',
        lastMessage: lastMsgText,
        lastMessageTime: lastTimeStr,
        isOutbound: lastMsg?.direction === 'outbound',
        isRead: lastMsg?.status === 'read',
        unreadCount: 0,
        isActive: c.id === selectedConversationId,
        isWindowClosed: windowClosed,
        windowExpiresAt: c.window_expires_at,
      };
    });
  }, [conversations, search, filterStatus, selectedConversationId]);

  // Transform messages to UI format (single conversation)
  const messageGroups = useMemo((): MessageGroup[] => {
    const messages = messagesQuery.data?.data || [];
    
    const groups: MessageGroup[] = [];
    let currentGroup: MessageGroup | null = null;

    messages.forEach((msg: any) => {
      const msgDate = new Date(msg.created_at);
      const dateStr = msgDate.toLocaleDateString('pt-BR');
      const today = new Date().toLocaleDateString('pt-BR');
      
      if (!currentGroup || currentGroup.date !== dateStr) {
        currentGroup = { 
          date: dateStr, 
          messages: [],
          isToday: dateStr === today
        };
        groups.push(currentGroup);
      }

      const messageData: MessageData = {
        kind: 'message',
        id: msg.id,
        text: msg.text || '',
        time: new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        direction: msg.direction,
        status: msg.status,
        type: msg.type,
        templateId: msg.template_id,
        conversationId: msg.conversation_id,
      };

      currentGroup.messages.push(messageData);
    });

    return groups;
  }, [messagesQuery.data]);

  // Handle send message
  const handleSendMessage = async (text: string) => {
    if (!selectedConversation?.contacts?.phone) return;
    
    try {
      setSending(true);
      await sendTextMessage({ 
        to: selectedConversation.contacts.phone, 
        type: 'text', 
        text 
      });
      messagesQuery.refetch();
    } catch (e) {
      console.error('Erro ao enviar:', e);
    } finally {
      setSending(false);
    }
  };

  // Check if can send
  const canSend = selectedConversation?.window_expires_at 
    ? new Date(selectedConversation.window_expires_at) > new Date()
    : false;

  return (
    <div className="h-full w-full overflow-hidden" style={{ backgroundColor: whatsappColors.bg }}>
      <div className="h-full w-full flex">
        {/* Conversations List - Resizable */}
        <ResizablePanel defaultWidth={400} minWidth={300} maxWidth={600}>
          <ConversationList
            conversations={conversationItems}
            onSelectConversation={(id) => navigate(`/conversations/${id}`)}
            searchValue={search}
            onSearchChange={setSearch}
            filterStatus={filterStatus}
            onFilterChange={setFilterStatus}
            isLoading={conversationsQuery.isLoading}
            error={conversationsQuery.isError ? 'Falha ao carregar' : undefined}
            onRetry={() => conversationsQuery.refetch()}
          />
        </ResizablePanel>

        {/* Chat Area */}
        {selectedConversationId && selectedConversation ? (
          <div className="flex-1 h-full flex flex-col min-w-0">
            <ChatArea
              messageGroups={messageGroups}
              contactName={selectedConversation.contacts?.name || selectedConversation.contacts?.phone || 'Conversa'}
              contactInitial={(selectedConversation.contacts?.name || selectedConversation.contacts?.phone || '?')[0]?.toUpperCase()}
              isOnline={selectedConversation.status === 'open' && !isWindowClosed}
              isLoading={messagesQuery.isLoading}
              error={messagesQuery.isError ? 'Falha ao carregar' : undefined}
              onRetry={() => messagesQuery.refetch()}
            />
            <ChatComposer
              onSendMessage={handleSendMessage}
              canSend={canSend && !sending}
              placeholder={canSend ? 'Digite uma mensagem' : 'Janela encerrada - use template'}
              disabled={!canSend || sending}
            />
          </div>
        ) : (
          <div 
            className="flex-1 h-full flex items-center justify-center"
            style={{ backgroundColor: whatsappColors.bg, borderLeft: `1px solid ${whatsappColors.borderDark}` }}
          >
            <div className="text-center max-w-md px-8">
              <div className="mb-6">
                <svg width="320" height="250" viewBox="0 0 320 250" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="160" cy="125" r="100" fill="#d9fdd3" opacity="0.2"/>
                  <path d="M160 60c-35.9 0-65 29.1-65 65s29.1 65 65 65 65-29.1 65-65-29.1-65-65-65zm0 115c-27.6 0-50-22.4-50-50s22.4-50 50-50 50 22.4 50 50-22.4 50-50 50z" fill="#00a884" opacity="0.6"/>
                </svg>
              </div>
              <div className="text-[32px] font-light mb-3" style={{ color: whatsappColors.textPrimary }}>
                WhatsApp Web
              </div>
              <div className="text-[14px] leading-relaxed" style={{ color: whatsappColors.textSecondary }}>
                Envie e receba mensagens sem manter seu telefone conectado.<br/>
                Selecione uma conversa para começar.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function
function formatTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}
